import React, { useState, useEffect } from 'react';
import {
	Card,
	CardBody,
	CardHeader,
} from '../../../../_metronic/_partials/controls';
import {
	Button,
	TextField,
	MuiThemeProvider,
	createMuiTheme,
	FormControl,
	InputLabel,
	FormHelperText,
} from '@material-ui/core';
import Select from 'react-select';
import { useHistory, useParams } from 'react-router-dom';
import {
	deleteBoat,
	getBoatById,
	postBoat,
	updateBoat,
} from '../../../../api/boat';
import { useSkeleton } from '../../../hooks/useSkeleton';
import PreviewDialog from '../../../components/dialogs/PreviewDialog';
import { SERVER_URL } from '../../../../utils';
import { alertError, alertSuccess } from '../../../../utils/logger';
import ConfirmDialog from '../../../components/dialogs/ConfirmDialog';
import { getSailors } from '../../../../api/sailor';
import { getBoatTypes } from '../../../../api/boattype';

// Create theme for delete button (red)
const theme = createMuiTheme({
	palette: {
		secondary: {
			main: '#F64E60',
		},
	},
});

function getEmptyBoat() {
	return {
		fullName: '',
		sailor: null,
		image: '',
		boatType: null,
		boatClass: '',
		boatNumber: '',
		sailNumber: '',
	};
}

export default function EditBoatsPage() {
	const [boat, setBoat] = useState(getEmptyBoat());
	const [sailors, setSailors] = useState(null);
	const [boatTypes, setBoatTypes] = useState(null);
	const [selectedImage, setSelectedImage] = useState(null);
	const [openPreviewImage, setOpenPreviewImage] = useState(false);
	const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
	const boatId = useParams().id;
	const history = useHistory();

	const {
		isLoading: isLoadingData,
		disableLoading: disableLoadingData,
		ContentSkeleton,
	} = useSkeleton();

	useEffect(() => {
		getSailors().then((res) => {
			if (res.status === 200) {
				setSailors(res.data);
			}
		});
		getBoatTypes().then((res) => {
			if (res.status === 200) {
				setBoatTypes(res.data);
			}
		});
		if (!boatId) {
			disableLoadingData();
			return;
		}
		getBoatById(boatId)
			.then((res) => {
				if (res.status === 200) {
					setBoat(res.data);
					disableLoadingData();
				}
			})
			.catch((error) => {
				alertError({ error: error, customMessage: 'Could not get boat.' });
				history.push('/boats');
			});
	}, [boatId, disableLoadingData, history]);

	function saveBoat() {
		if (!boatId) {
			postBoat(boat, selectedImage)
				.then((res) => {
					if (res.status === 201) {
						alertSuccess({
							title: 'Saved!',
							customMessage: 'Boat successfully created.',
						});
						history.push('/boats');
					}
				})
				.catch((error) => {
					alertError({ error: error, customMessage: 'Could not save boat.' });
				});
		} else {
			updateBoat(boatId, boat, selectedImage)
				.then((res) => {
					if (res.status === 200) {
						alertSuccess({
							title: 'Saved!',
							customMessage: 'Changes successfully saved.',
						});
						history.push('/boats');
					}
				})
				.catch((error) => {
					alertError({
						error: error,
						customMessage: 'Could not save changes.',
					});
				});
		}
	}

	const handleChange = (element) => (event) => {
		setBoat({ ...boat, [element]: event.target.value });
	};

	const handleChangeSailors = (new_selected) => {
		setBoat((prevBoat) => ({
			...prevBoat,
			sailor: new_selected?.value || null,
		}));
	};

	const handleChangeBoatTypes = (new_selected) => {
		setBoat((prevBoat) => ({
			...prevBoat,
			boatType: new_selected?.value || null,
		}));
	};

	if (isLoadingData) return <ContentSkeleton />;
	else
		return (
			<>
				<Card>
					<CardHeader title='Edit boat'></CardHeader>
					<CardBody>
						<TextField
							id={`fullName`}
							label='Boat name'
							value={boat.fullName}
							onChange={handleChange('fullName')}
							InputLabelProps={{
								shrink: true,
							}}
							margin='normal'
							variant='outlined'
							required
						/>
						<TextField
							id={`boatNumber`}
							label="Boat Number"
							value={boat.boatNumber}
							onChange={handleChange("boatNumber")}
							InputLabelProps={{
								shrink: true,
							}}
							margin="normal"
							variant="outlined"
							required
						/>
						<TextField
							id={`sailNumber`}
							label="Sail Number"
							value={boat.sailNumber}
							onChange={handleChange("sailNumber")}
							InputLabelProps={{
								shrink: true,
							}}
							margin="normal"
							variant="outlined"
							required
						/>

						<FormControl style={{ width: '100%' }}>
							<InputLabel id='demo-simple-select-standard-label'>
								Content type
							</InputLabel>
							<Select
								options={boatTypes?.map((boatType) => ({
									label: boatType.fullName,
									value: boatType._id,
								}))}
								value={boatTypes
									?.filter((boatType) => boatType._id === boat.boatType)
									.map((boatType) => ({
										label: boatType.fullName,
										value: boatType._id,
									}))}
								onChange={handleChangeBoatTypes}
								isClearable
								isSearchable
								placeholder={`Select boat type...`}
								className='mb-3 mt-3'
							/>
						</FormControl>
						
						<TextField
							id={`boatClass`}
							label="Boat Class"
							value={boat.boatClass}
							onChange={handleChange("boatClass")}
							InputLabelProps={{
								shrink: true,
							}}
							margin="normal"
							variant="outlined"
						/>

						<FormControl style={{ width: '100%' }}>
							<InputLabel id='demo-simple-select-standard-label'>
								Content type
							</InputLabel>
							<Select
								options={sailors?.map((sailor) => ({
									label: sailor.fullName,
									value: sailor._id,
								}))}
								value={sailors
									?.filter((sailor) => sailor._id === boat.sailor)
									.map((sailor) => ({
										label: sailor.fullName,
										value: sailor._id,
									}))}
								onChange={handleChangeSailors}
								isClearable
								isSearchable
								placeholder={`Select sailor...`}
								className='mb-3 mt-3'
							/>
						</FormControl>
						<label className='mt-4' htmlFor={'upload-image'}>
							<input
								style={{ display: 'none' }}
								id={'upload-image'}
								name={'upload-image'}
								type='file'
								onChange={(e) => {
									setSelectedImage(e.target.files[0]);
								}}
							/>
							<Button
								style={{ marginRight: '15px' }}
								color='secondary'
								component='span'
								variant='outlined'>
								{selectedImage || boat.image !== ''
									? 'Change image'
									: 'Upload image'}
							</Button>
						</label>
						{(selectedImage || boat.image !== '') && (
							<>
								<Button
									onClick={() => setOpenPreviewImage(true)}
									style={{ marginRight: '15px' }}
									variant='outlined'>
									{'Preview image'}
								</Button>
								<PreviewDialog
									title={'Preview image'}
									open={openPreviewImage}
									setOpen={setOpenPreviewImage}
									src={
										selectedImage
											? URL.createObjectURL(selectedImage)
											: `${SERVER_URL}/${boat.image}`
									}
								/>
							</>
						)}
					</CardBody>
				</Card>
				<Button
					onClick={() => history.push('/boats')}
					variant='outlined'
					style={{ marginRight: '20px' }}>
					Back
				</Button>
				<Button
					onClick={() => saveBoat()}
					variant='outlined'
					color='primary'
					style={{ marginRight: '20px' }}>
					Save boat
				</Button>
				{boatId && (
					<>
						<MuiThemeProvider theme={theme}>
							<Button
								onClick={() => setOpenConfirmDialog(true)}
								variant='outlined'
								color='secondary'>
								Delete boat
							</Button>
						</MuiThemeProvider>

						<ConfirmDialog
							title={'Are you sure you want to delete this boat?'}
							open={openConfirmDialog}
							setOpen={setOpenConfirmDialog}
							onConfirm={() => {
								deleteBoat(boatId)
									.then((res) => {
										if (res.status === 204) {
											alertSuccess({
												title: 'Deleted!',
												customMessage: 'Boat deleted successfully',
											});
											history.push('/boats');
										}
									})
									.catch((error) => {
										alertError({
											error: error,
											customMessage: 'Could not delete boat.',
										});
									});
							}}
						/>
					</>
				)}
			</>
		);
}
